/*
 * ---------------------------------------------------------------------------------------------------------------------
 * HOMEPAGE AD BLOCKS – SCROLL-LINKED FADE TO BLACK
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * As a banner section scrolls up, its PHOTO darkens to black in step with the scroll. The title and the
 * Explore / Shop button stay in front of the black and remain bright.
 *
 * Timing (same as the client's reference demo):
 *   - starts when the bottom edge of the section reaches `start` of the window height (default 90%)
 *   - fully dark when the bottom edge reaches `end` (default 10%, about the logo)
 *   - curve: progress^1.6 – very gentle at first, deepening as the section leaves
 *
 * The darkness is computed directly from the section's position on every scroll frame. There is deliberately NO
 * extra smoothing/easing towards a target: Lenis already smooths the scroll, and a per-frame "catch-up" is what made
 * the old effect lag and run at different speeds (and faster on 120 Hz screens).
 *
 * Where the black layer goes: Prestige banners are `.content-over-media` grids (photo first, text after). The layer
 * is inserted right after the photo, so it paints over the photo but under the text. If a section has no such grid,
 * the layer covers the whole section.
 */
(() => {
  const config = Object.assign({
    enabled: true,
    start: 90,      // % of window height, measured from the top
    end: 10,        // % of window height, measured from the top
    maxDark: 100,   // % opacity of the black at the end
    curve: 1.6,
    selector: '.shopify-section--slideshow, .shopify-section--image-with-text-overlay'
  }, window.adBlockFadeSettings || {});

  if (!config.enabled || !config.selector) return;

  const MEDIA = ':scope > :is(picture, img, video, iframe, svg, video-media, image-parallax)';
  let items = [];
  let ticking = false;

  const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

  const createLayer = () => {
    const layer = document.createElement('div');
    layer.className = 'ad-fade-layer';
    layer.setAttribute('aria-hidden', 'true');
    return layer;
  };

  const collect = () => {
    document.querySelectorAll('.ad-fade-layer').forEach((l) => l.remove());
    document.querySelectorAll('.ad-block').forEach((b) => b.classList.remove('ad-block', 'ad-block--whole'));

    items = Array.from(document.querySelectorAll('#main > .shopify-section'))
      .filter((section) => section.matches(config.selector))
      .map((section) => {
        section.classList.add('ad-block');
        const layers = [];

        section.querySelectorAll('.content-over-media').forEach((grid) => {
          const media = Array.from(grid.querySelectorAll(MEDIA)).pop();
          if (!media) return;
          const layer = createLayer();
          media.after(layer); // above the photo, below the title / button
          layers.push(layer);
        });

        if (!layers.length) {
          section.classList.add('ad-block--whole');
          const layer = createLayer();
          section.appendChild(layer);
          layers.push(layer);
        }

        return { section, layers, last: -1 };
      });
  };

  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    const startLine = vh * (config.start / 100);
    const endLine = vh * (config.end / 100);
    const span = Math.max(1, startLine - endLine);
    const max = config.maxDark / 100;

    items.forEach((item) => {
      const bottom = item.section.getBoundingClientRect().bottom;
      const progress = clamp((startLine - bottom) / span);
      const value = Math.round(Math.pow(progress, config.curve) * max * 1000) / 1000;

      if (value !== item.last) {
        item.last = value;
        item.layers.forEach((layer) => (layer.style.opacity = value));
      }
    });
  };

  const requestUpdate = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  };

  const init = () => {
    collect();
    document.documentElement.classList.add('ad-fade-enabled');
    update();

    // capture: also catches scrolling on a wrapper element (smooth-scroll / custom scrollbar libraries)
    document.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections can be added, removed or re-ordered without a page load
  if (window.Shopify && Shopify.designMode) {
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((evt) => {
      document.addEventListener(evt, () => setTimeout(() => { collect(); update(); }, 0));
    });
  }
})();
